import { useState } from 'react';
import { Microscope, CheckCircle2 } from 'lucide-react';
import { patientApi } from '../api';
import type { MarketplaceProvider, ProviderPricing } from '../types';
import BookingModal from './BookingModal';

interface Props {
  provider: MarketplaceProvider;
  pricing: ProviderPricing[];
  open: boolean;
  onClose: () => void;
}

export default function BookLabForm({ provider, pricing, open, onClose }: Props) {
  const [testName, setTestName] = useState('');
  const [collectionMode, setCollectionMode] = useState('WALK_IN');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<any>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!testName) return;
    setSubmitting(true);
    try {
      const result = await patientApi.bookLab({
        providerId: provider.id,
        testName,
        collectionMode,
        notes: notes || undefined,
      });
      setSuccess(result);
    } catch {
      alert('Booking failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <BookingModal title="Test Booked" open={open} onClose={onClose}>
        <div className="text-center py-4 space-y-3">
          <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
          <h3 className="text-lg font-bold text-gray-900">Lab Test Booked!</h3>
          <p className="text-sm text-gray-600">
            Your test at <span className="font-semibold">{provider.name}</span> has been booked.
          </p>
          <div className="bg-purple-50 rounded-xl p-3">
            <p className="text-xs text-purple-600 uppercase tracking-wider font-semibold">Sample Code</p>
            <p className="text-xl font-bold text-purple-700 mt-1 font-mono">{success.sampleCode}</p>
          </div>
          <p className="text-xs text-gray-500">{testName} &middot; {collectionMode === 'HOME_COLLECTION' ? 'Home Collection' : 'Walk-in'}</p>
          <button onClick={onClose} className="btn-primary w-full mt-4">Done</button>
        </div>
      </BookingModal>
    );
  }

  return (
    <BookingModal title="Book Lab Test" open={open} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
          <Microscope className="w-5 h-5 text-purple-600" />
          <div>
            <p className="text-sm font-semibold text-gray-900">{provider.name}</p>
            <p className="text-xs text-gray-500">{provider.city}</p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Test Name *</label>
          {pricing.length > 0 ? (
            <select className="input" value={testName} onChange={e => setTestName(e.target.value)} required>
              <option value="">Select a test...</option>
              {pricing.map(p => (
                <option key={p.service} value={p.service}>
                  {p.service} — &#8377;{p.price.toLocaleString('en-IN')}
                </option>
              ))}
            </select>
          ) : (
            <input
              className="input"
              placeholder="e.g. Complete Blood Count, Lipid Profile..."
              value={testName}
              onChange={e => setTestName(e.target.value)}
              required
            />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Collection Mode</label>
          <select className="input" value={collectionMode} onChange={e => setCollectionMode(e.target.value)}>
            <option value="WALK_IN">Walk-in at Lab</option>
            <option value="HOME_COLLECTION">Home Collection</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
          <textarea
            className="input"
            rows={2}
            placeholder="Fasting required, prescribed by doctor, etc..."
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />
        </div>

        <p className="text-[10px] text-gray-400 leading-relaxed">By confirming, you consent to sharing relevant medical details with this lab per NeoHealthCard policy. All access is audit logged.</p>

        <button type="submit" disabled={submitting || !testName} className="btn-primary w-full">
          {submitting ? 'Booking...' : 'Book Test'}
        </button>
      </form>
    </BookingModal>
  );
}
